import React, {useEffect, useMemo, useRef, useState} from 'react';
import {logoTextWhite} from '../../../assets/images';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import styles from './PosterInput.module.css';

export interface PosterInputText {
  heading: string;
  replace: string;
  remove: string;
  emptyTitle: string;
  emptyHint: string;
  hint: string;
  hintEmpty: string;
}

interface Props {
  text: PosterInputText;
  image?: File;
  existingImage?: string;
  onChange: (file?: File) => void;
  aspectRatio?: string;
  maxFrameWidth?: number;
  fit?: 'contain' | 'cover';
  headingClassName?: string;
}

const getFileNameFromUrl = (url: string) => {
  try {
    const path = new URL(url).pathname;
    const name = decodeURIComponent(path.split('/').pop() || '');
    return name || url;
  } catch {
    return url.split('/').pop() || url;
  }
};

const PosterInput = React.memo(
  ({
    text,
    image,
    existingImage,
    onChange,
    aspectRatio,
    maxFrameWidth,
    fit,
    headingClassName,
  }: Props) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [previewFailed, setPreviewFailed] = useState(false);
    const [previewLoaded, setPreviewLoaded] = useState(false);

    const previewUrl = useMemo(
      () => (image ? URL.createObjectURL(image) : existingImage),
      [image, existingImage],
    );

    useEffect(() => {
      setPreviewFailed(false);
      setPreviewLoaded(false);
    }, [previewUrl]);

    const onFileChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
      const file = ev.target.files?.[0];
      if (file && file.type.includes('image')) {
        onChange(file);
      }
      ev.target.value = '';
    };

    const openPicker = () => {
      fileInputRef.current?.click();
    };

    const fileName = image
      ? image.name
      : existingImage
      ? getFileNameFromUrl(existingImage)
      : '';

    const frameStyle = {aspectRatio, maxWidth: maxFrameWidth};

    return (
      <div>
        <p className={headingClassName ?? styles.title}>{text.heading}</p>
        {previewUrl ? (
          <div className={styles.frame} style={frameStyle}>
            {previewFailed ? (
              <img src={logoTextWhite} alt="" className={styles.imageMissing} />
            ) : (
              <>
                <img
                  src={previewUrl}
                  alt=""
                  className={`${styles.image} ${
                    fit === 'cover' ? styles.imageCover : ''
                  }`}
                  onLoad={() => setPreviewLoaded(true)}
                  onError={() => setPreviewFailed(true)}
                />
                {!previewLoaded && (
                  <span className={styles.imageLoading}>
                    <LoadingSpinner size={32} />
                  </span>
                )}
              </>
            )}
            <div className={styles.bar}>
              <span className={styles.fileName}>
                {previewFailed ? '' : fileName}
              </span>
              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.replace}
                  onClick={openPicker}>
                  {text.replace}
                </button>
                {image && (
                  <button
                    type="button"
                    className={styles.remove}
                    onClick={() => onChange()}>
                    {text.remove}
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className={`${styles.frame} ${styles.dropzone}`}
            style={frameStyle}
            onClick={openPicker}>
            <svg
              width="30"
              height="30"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#7d8b94"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="m21 15-5-5L5 21" />
            </svg>
            <span className={styles.dropTitle}>{text.emptyTitle}</span>
            <span className={styles.dropHint}>{text.emptyHint}</span>
          </button>
        )}
        <p className={styles.hint}>{previewUrl ? text.hint : text.hintEmpty}</p>
        <input
          ref={fileInputRef}
          type="file"
          onChange={onFileChange}
          accept="image/jpg,image/jpeg,image/png"
          style={{display: 'none'}}
        />
      </div>
    );
  },
);

export default PosterInput;
